import React, { Component, Children } from 'react';
import PropTypes from 'prop-types';
import {
  MultiGrid, AutoSizer, CellMeasurer, CellMeasurerCache,
} from 'react-virtualized';
import classnames from 'classnames';
import { fromJS, Map } from 'immutable';
import { parse } from 'json2csv';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import {
  Formik, Form,
} from 'formik';
import { diff } from 'deep-object-diff';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { ResultGridColumnModal } from './ResultGridColumnModal';
import { resolveDisplayName } from './resultGridUtils';
import { downloadText } from './textDownloader';
import { ColumnChooserIcon } from '../icon/ColumnChooserIcon';
import { DownloadDataIcon } from '../icon/DownloadDataIcon';
import { IconWithTooltip } from '../icon/IconWithTooltip';
import { EditConfirmationModal } from '../form/EditConfirmationModal';

const normalize = map => JSON.parse(JSON.stringify(map));

export class ResultGrid extends Component {
  constructor(props) {
    super(props);
    this.onResize = this.onResize.bind(this);
    this.setSort = this.setSort.bind(this);
    this.renderColumnChooser = this.renderColumnChooser.bind(this);
    this.toggleRenderColumnChooser = this.toggleRenderColumnChooser.bind(this);
    this.getVisibleColumns = this.getVisibleColumns.bind(this);
    this.handleColumnsToggle = this.handleColumnsToggle.bind(this);
    this.handleDownload = this.handleDownload.bind(this);
    this.renderGridControls = this.renderGridControls.bind(this);
    this.renderDownload = this.renderDownload.bind(this);
    this.renderContent = this.renderContent.bind(this);
    this.renderEditContent = this.renderEditContent.bind(this);
    this.renderGrid = this.renderGrid.bind(this);
    this.renderEdit = this.renderEdit.bind(this);
    this.renderSave = this.renderSave.bind(this);
    this.renderCancel = this.renderCancel.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.normalizeData = this.normalizeData.bind(this);
    this.measureCache = new CellMeasurerCache({
      defaultWidth: 150,
      fixedWidth: true,
    });
    this.state = {
      columnChooserVisible: false,
      sort: fromJS([]),
    };
    // eslint-disable-next-line react/no-unused-class-component-methods
    this.id = `i${uuid()}`;
  }

  componentDidMount() {
    this.setSort();
  }

  componentDidUpdate({ sort: oldSort }) {
    const { sort } = this.props;
    if (oldSort !== sort) {
      this.setSort();
    }
  }

  setSort() {
    const { sort } = this.props;
    this.setState({ sort });
  }

  onResize() {
    this.grid.recomputeGridSize();
    this.measureCache.clearAll();
  }

  getVisibleColumns() {
    const { children, visibleColumns, columnChooser } = this.props;
    return Children.toArray(children)
      .filter(o => o)
      .filter(({ props: { id, alwaysVisible } }) => {
        if (!columnChooser || alwaysVisible) {
          return true;
        }
        return visibleColumns.includes(id);
      });
  }

  toggleRenderColumnChooser() {
    this.setState(({ columnChooserVisible: prev }) => ({ columnChooserVisible: !prev }));
  }

  handleDownload() {
    const { data, downloadPrefix, downloadSearchParams } = this.props;

    const now = moment();

    const visibleFields = this.getVisibleColumns()
      .filter(({ props: { downloadable } }) => downloadable)
      .map(({ props }) => ({ value: props.id, label: resolveDisplayName(props) }));
    const fields = [{ value: '___sp___', label: 'Search Parameters' }, ...visibleFields];

    const augmentedData = data.toJS();
    if (downloadSearchParams) {
      augmentedData.unshift({ ___sp___: downloadSearchParams });
    }

    const opts = { fields, delimiter: '\t' };
    const csv = parse(augmentedData, opts);
    const filename = now.format(`[${downloadPrefix}-]YYYY-MM-DD_HH-mm-ss_ZZ[.tsv]`);

    downloadText({ filename, text: csv });
  }

  handleColumnsToggle(params) {
    const { handleColumnsToggle } = this.props;
    const changed = handleColumnsToggle(params);
    if (changed) {
      this.onResize();
    }
  }

  renderGridControls(sort, formik) {
    const columnChooser = this.renderColumnChooser(sort);
    const download = this.renderDownload();
    const editToggle = this.renderEdit();
    const save = this.renderSave(formik);
    const cancel = this.renderCancel(formik);

    if (columnChooser || download || editToggle || save || cancel) {
      return (
        <div className="haz-grid-controls">
          {editToggle}
          {save}
          {cancel}
          {columnChooser}
          {download}
        </div>
      );
    }
    return null;
  }

  renderDownload() {
    const { download } = this.props;

    if (!download) {
      return null;
    }

    return (
      <div className="haz-grid-control">
        <DownloadDataIcon handleClick={this.handleDownload} />
      </div>
    );
  }

  renderEdit() {
    const { edit, editing, handleEditToggle } = this.props;

    if (!edit || editing) {
      return null;
    }

    return (
      <div className="haz-grid-control">
        <IconWithTooltip
          handleClick={() => {
            this.onResize();
            handleEditToggle(true);
          }}
          ariaLabel="Edit Grid"
          tooltip="Bulk Edit"
          icon="edit"
        />
      </div>
    );
  }

  // eslint-disable-next-line class-methods-use-this
  renderSave(formik) {
    if (!formik || !formik.dirty) {
      return null;
    }

    return (
      <div className="haz-grid-control">
        <IconWithTooltip
          handleClick={() => formik.submitForm()}
          ariaLabel="Save Updates"
          tooltip="Save"
          icon="save"
        />
      </div>
    );
  }

  renderCancel(formik) {
    const { editing, handleEditToggle } = this.props;

    if (!editing) {
      return null;
    }

    return (
      <div className="haz-grid-control">
        <IconWithTooltip
          handleClick={() => {
            formik.resetForm();
            this.onResize();
            handleEditToggle(false);
          }}
          ariaLabel="Cancel Edit"
          tooltip="Cancel Edit"
          icon="undo"
        />
      </div>
    );
  }

  renderColumnChooser(sort) {
    const {
      children, columnChooser, visibleColumns, defaultColumns, defaultSort, minColumns,
    } = this.props;
    const { columnChooserVisible } = this.state;

    if (!columnChooser) {
      return null;
    }

    return (
      <div className="haz-grid-control haz-columns" aria-live="polite" aria-atomic="true" aria-relevant="additions">
        <ColumnChooserIcon handleClick={this.toggleRenderColumnChooser} />
        <ResultGridColumnModal
          toggleOpen={this.toggleRenderColumnChooser}
          handleColumnsToggle={this.handleColumnsToggle}
          isOpen={columnChooserVisible}
          visibleColumns={visibleColumns}
          sort={sort}
          defaultColumns={defaultColumns}
          defaultSort={defaultSort}
          minColumns={minColumns}
        >
          {children}
        </ResultGridColumnModal>
      </div>
    );
  }

  normalizeData(form, formik) {
    const { initialValues, openEditConfirmation, formToJson } = this.props;
    formik.resetForm();
    formik.setValues(form);

    const initialValuesObj = initialValues.toJS();

    const extItems = {};
    let existing = { items: extItems };
    Object.keys(initialValuesObj.items).forEach((id) => {
      extItems[`${id}`] = formToJson(fromJS(initialValuesObj.items[`${id}`]));
    });

    existing = normalize(existing);

    const items = {};
    let updated = { items };
    Object.keys(form.items).forEach((id) => {
      items[`${id}`] = formToJson(fromJS(form.items[`${id}`]));
    });

    updated = normalize(updated);

    openEditConfirmation({ formData: updated, initialFormData: existing });
  }

  handleSubmit({
    existing, updated, differences, formik,
  }) {
    const { handleSubmit } = this.props;

    const e = existing.toJS();
    const u = updated.toJS();

    let sparse = diff(e, u);

    differences
      .filter(({ kind }) => kind === 'D')
      .forEach(({ path }) => {
        const id = path[1];
        const field = path[2];
        // eslint-disable-next-line prefer-destructuring
        let deleteFields = sparse.items[id].deleteFields;
        if (!deleteFields) {
          deleteFields = [];
          sparse.items[id].deleteFields = deleteFields;
        }
        deleteFields.push(field);
      });

    sparse = normalize(sparse);
    handleSubmit(sparse, formik);
  }

  renderGrid(renderers, determineWidth, sort, formik) {
    const {
      editConfirmationOpen,
      hazardName,
      saving,
      handleCancel,
      formData,
      initialFormData,
    } = this.props;

    return (
      <React.Fragment>
        <EditConfirmationModal
          isOpen={editConfirmationOpen}
          title={hazardName}
          saving={saving}
          onCancel={handleCancel}
          onSave={({ existing, updated, differences }) => this.handleSubmit({
            existing, updated, differences, formik,
          })}
          updated={formData}
          existing={initialFormData}
        />
        {this.renderGridControls(sort, formik)}
        <AutoSizer onResize={this.onResize}>
          {({ width, height }) => (
            <MultiGrid
              ref={(ref) => { this.grid = ref; }}
              cellRenderer={cellInfo => renderers.get(cellInfo.rowIndex).get(cellInfo.columnIndex)({ ...cellInfo, gridWidth: width })}
              columnCount={renderers.get(0).size}
              estimatedColumnSize={75}
              columnWidth={({ index }) => determineWidth(index, width)}
              rowHeight={this.measureCache.rowHeight}
              height={height}
              rowCount={renderers.size}
              width={width}
              fixedRowCount={1}
              enableFixedRowScroll
              deferredMeasurementCache={this.measureCache}
              containerRole="row"
            />
          )}
        </AutoSizer>
      </React.Fragment>
    );
  }

  renderContent(renderers, determineWidth, sort) {
    return (
      <div className="haz-grid" aria-live="polite">
        {this.renderGrid(renderers, determineWidth, sort)}
      </div>
    );
  }

  renderEditContent(data, renderers, determineWidth, sort) {
    const { initialValues } = this.props;
    if (!initialValues.size) {
      return null;
    }
    const iv = initialValues.toJS();
    return (
      <Formik
        initialValues={iv}
        onSubmit={this.normalizeData}
        render={formik => (
          <Form className="haz-grid" aria-live="polite">
            {this.renderGrid(renderers, determineWidth, sort, formik)}
          </Form>
        )}
      />
    );
  }

  render() {
    const { data, editing } = this.props;
    const { sort: rawSort } = this.state;

    const children = this.getVisibleColumns();

    // const ids = children.map(({ props }) => props.id);
    //
    // const sort = rawSort.filter(def => ids.indexOf(def.get('id')) >= 0);
    const sort = rawSort;
    const headerRenderers = [];

    const widths = children.map(({ props: { width } }) => width);

    const determineWidth = (index, width) => {
      const requestedWidth = widths.reduce((accumulator, currentValue) => accumulator + currentValue);
      let addl = 0;
      if (width > requestedWidth) {
        const extra = width - requestedWidth;
        addl = extra / widths.length;
      }
      return widths[index] + addl;
    };

    children.forEach((column) => {
      // const { id, sortable } = column.props;
      // const sortDef = getSortDefinition(sort, id, sortable);
      headerRenderers.push(({
        key, style, columnIndex, parent, rowIndex, gridWidth,
      }) => (
        <CellMeasurer
          cache={this.measureCache}
          columnIndex={columnIndex}
          key={key}
          parent={parent}
          rowIndex={rowIndex}
        >
          <div
            className="haz-grid-column-header"
            role="columnheader"
            style={{
              ...style,
              width: determineWidth(columnIndex, gridWidth),
            }}
          >
            <div className="haz-grid-column-title" title={column.props.children}>
              {/* <div id={`${this.id}${columnIndex}`}> */}
              {column}
            </div>
          </div>
          {/* <UncontrolledTooltip placement="top" target={`${this.id}${columnIndex}`}>{column.props.children}</UncontrolledTooltip> */}
        </CellMeasurer>
      ));
    });
    let prevWarning = false;
    let renderers = [];
    data.forEach((dataRow, index) => {
      const row = [];
      const cells = [];
      let warning = false;
      children.filter(o => o).forEach((column) => {
        const { id, cell } = column.props;
        const cellJsx = cell({
          id, row: dataRow, value: dataRow.get(id), index,
        });

        if (!warning && cellJsx && cellJsx.props && cellJsx.props.warning) {
          const cellWarning = !!cellJsx.props.warning;
          if (cellWarning) {
            warning = true;
          }
        }

        cells.push({ cellJsx, column });
      });
      cells.forEach(({ cellJsx, column }) => {
        const { id } = column.props;
        let cellWarning = false;
        let cellInfo = false;
        if (cellJsx && cellJsx.props) {
          if (cellJsx.props.warning) {
            cellWarning = !!cellJsx.props.warning;
          }
          if (cellJsx.props.info) {
            cellInfo = !!cellJsx.props.info;
          }
        }
        const className = classnames(
          {
            'haz-grid-cell-error': warning,
            'haz-grid-cell-error-prev': prevWarning,
            'haz-grid-cell-error-fill': cellWarning,
            'haz-grid-cell-info-fill': cellInfo,
            'haz-grid-cell': true,
            'haz-odd': index % 2 === 1,
          },
        );

        const cellRenderer = ({
          key, style, columnIndex, parent, rowIndex, gridWidth,
        }) => (
          <CellMeasurer
            cache={this.measureCache}
            columnIndex={columnIndex}
            key={key}
            parent={parent}
            rowIndex={rowIndex}
          >
            <div
              className={className}
              role="gridcell"
              aria-label={(dataRow.get(id) !== undefined) ? id : `Empty ${id}`}
              style={{
                ...style,
                width: determineWidth(columnIndex, gridWidth),
              }}
            >
              {cellJsx}
            </div>
          </CellMeasurer>
        );
        row.push(cellRenderer);
      });
      renderers.push(fromJS(row));

      prevWarning = warning;
    });

    renderers = fromJS(renderers).insert(0, fromJS(headerRenderers));

    if (editing) {
      return this.renderEditContent(data, renderers, determineWidth, sort);
    }

    return this.renderContent(renderers, determineWidth, sort);
  }
}

const defaultSort = fromJS([]);
const defaultColumns = fromJS([]);
const defaultMap = Map();
const minColumns = fromJS([]);

ResultGrid.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element, PropTypes.node]).isRequired,
  data: ImmutablePropTypes.list.isRequired,
  sort: ImmutablePropTypes.list,
  columnChooser: PropTypes.bool,
  visibleColumns: ImmutablePropTypes.list,
  handleColumnsToggle: PropTypes.func,
  download: PropTypes.bool,
  downloadPrefix: PropTypes.string,
  downloadSearchParams: PropTypes.string,
  edit: PropTypes.bool,
  editing: PropTypes.bool,
  handleEditToggle: PropTypes.func,
  initialValues: ImmutablePropTypes.map,
  handleSubmit: PropTypes.func,
  editConfirmationOpen: PropTypes.bool,
  hazardName: PropTypes.string,
  saving: PropTypes.bool,
  handleCancel: PropTypes.func,
  formData: ImmutablePropTypes.map,
  initialFormData: ImmutablePropTypes.map,
  openEditConfirmation: PropTypes.func,
  formToJson: PropTypes.func,
  defaultColumns: ImmutablePropTypes.list,
  defaultSort: ImmutablePropTypes.list,
  minColumns: ImmutablePropTypes.list,
};

ResultGrid.defaultProps = {
  sort: defaultSort,
  columnChooser: false,
  visibleColumns: defaultColumns,
  handleColumnsToggle: () => {},
  download: false,
  downloadPrefix: 'grid',
  downloadSearchParams: '',
  edit: false,
  editing: false,
  handleEditToggle: () => {},
  initialValues: defaultMap,
  handleSubmit: () => {},
  editConfirmationOpen: false,
  hazardName: '',
  saving: false,
  handleCancel: () => {},
  formData: defaultMap,
  initialFormData: defaultMap,
  openEditConfirmation: () => {},
  formToJson: () => {},
  minColumns,
  defaultSort,
  defaultColumns,
};
